import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  TreeDeciduous, ArrowLeft, Link, Copy, Plus, 
  Clock, CheckCircle, XCircle, Shield, Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvitePage = ({ user }) => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");

  useEffect(() => {
    fetchData();
  }, [branchId]);

  const fetchData = async () => {
    try {
      const [invitesRes, branchRes] = await Promise.all([
        fetch(`${API}/invites/${branchId}`, { credentials: "include" }),
        fetch(`${API}/branches/${branchId}`, { credentials: "include" })
      ]);

      if (!branchRes.ok) {
        toast.error("Branch not found or access denied");
        navigate("/dashboard");
        return;
      }

      const branchData = await branchRes.json();
      setBranch(branchData);

      if (!branchData.is_admin) {
        toast.error("Admin access required");
        navigate(`/branch/${branchId}`);
        return;
      }

      if (invitesRes.ok) {
        const invitesData = await invitesRes.json();
        setInvites(invitesData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load invites");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await fetch(`${API}/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          branch_id: branchId,
          email: email || null,
          role: role
        })
      });

      if (!response.ok) throw new Error("Failed to create invite");

      const invite = await response.json();
      setInvites([invite, ...invites]);
      setEmail("");
      setRole("member");
      toast.success(`${role === "admin" ? "Admin" : "Member"} invite link created!`);
    } catch (error) {
      console.error("Error creating invite:", error);
      toast.error("Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const copyInviteLink = (invite) => {
    const link = `${window.location.origin}/invite/${invite.invite_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied to clipboard!");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
        return <CheckCircle className="w-4 h-4 text-success" />;
      case "expired":
        return <XCircle className="w-4 h-4 text-destructive" />;
      default:
        return <Clock className="w-4 h-4 text-warning" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/branch/${branchId}`)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-8 h-8 text-primary" />
              <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
            Invite Family Members
          </h1>
          <p className="text-lg text-muted-foreground">
            Share invite links to bring family members to the {branch?.name} branch
          </p>
        </div>

        {/* Create Invite Form */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-display text-xl">Create New Invite</CardTitle>
            <CardDescription>
              Generate a unique invite link that family members can use to join this branch.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger id="invite-role" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> Member - Can view and add family members
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" /> Admin - Full control over branch
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email (Optional)</Label>
                <Input
                  id="invite-email"
                  type="email"
                  data-testid="invite-email-input"
                  placeholder="family@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Add an email to track who the invite is for, or leave blank for a general invite.
                </p>
              </div>
              <Button 
                type="submit" 
                disabled={creating}
                data-testid="create-invite-btn"
                className="bg-primary text-primary-foreground rounded-full"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" /> Create Invite Link
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Invites List */}
        <div>
          <h2 className="font-display text-2xl font-semibold text-foreground mb-6">
            Invite History
          </h2>

          {invites.length === 0 ? (
            <Card className="bg-card rounded-2xl border border-border/60 p-12 text-center">
              <Link className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl font-medium text-foreground mb-2">
                No invites yet
              </h3>
              <p className="text-muted-foreground">
                Create an invite link to share with family members
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {invites.map((invite) => (
                <Card 
                  key={invite.invite_id}
                  className="bg-card rounded-2xl border border-border/60 shadow-sm"
                  data-testid={`invite-card-${invite.invite_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${invite.role === 'admin' ? 'bg-accent/10' : 'bg-primary/10'}`}>
                          {invite.role === 'admin' ? <Shield className="w-5 h-5 text-accent" /> : <Users className="w-5 h-5 text-primary" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(invite.status)}
                            <span className="font-medium capitalize">{invite.status}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${invite.role === 'admin' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`}>
                              {invite.role === 'admin' ? 'Admin' : 'Member'}
                            </span>
                          </div>
                          {invite.email && (
                            <p className="text-sm text-muted-foreground">
                              For: {invite.email}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Expires: {new Date(invite.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      {invite.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyInviteLink(invite)}
                          data-testid={`copy-invite-${invite.invite_id}`}
                          className="rounded-full"
                        >
                          <Copy className="w-4 h-4 mr-2" /> Copy Link
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InvitePage;
